/* @flow */
import React, { useContext, useCallback, useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { get } from "../common/fetch";
import Dimensions from "./Dimensions";
import { TourContext } from "../Tour";
import Protection from "../Protection";

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
  background-color: ${props => props.theme.main};
  ${({ tour }) =>
    tour != null &&
    css`
      filter: blur(2px);
    `};
`;

const View = styled.div`
  display: flex;
  overflow: hidden;
`;

const Tabs = styled(View)`
  height: 48px;
`;

const Chart = styled(View)`
  position: relative;
  flex: 1;
  border-bottom: 1px solid ${props => props.theme.borderMainColor};
`;

const Ads = styled(View)`
  height: 45px;
`;

const LandscapeControls = styled(View)`
  width: 123px;

  @media (min-width: 568px) {
    width: 143px;
  }
`;

const PortraitContainer = styled(View)`
  flex: 1;
`;

const ContainerInner = styled(View)`
  flex: 1;
  flex-direction: column;
`;

type Props = {
  tabs: React.Node,
  children: React.Node, // chart
  controls: React.Node,
  ads: React.Node,
  bonus?: React.Node,
  showControls?: boolean,
  isIOS?: boolean,
  appVersion: number,
  dealsToCompensate: Array,
  onCompensation: () => void,
  onCompensationCreated: () => void
};

function Landscape({
  tabs,
  controls,
  ads,
  bonus,
  children,
  dealsToCompensate,
  onCompensation,
  compensationUpdated,
  compensationCreated,
  onCompensationCreated,
  ...props
}: Props) {
  let tour = useContext(TourContext);
  const [showChart, setShowChart] = useState(true);

  const handleVisibilityChange = useCallback(()=>{
    if (document.hidden) {
      setShowChart(false);
    } else  {
      setShowChart(true);
    }
  },[]);

  useEffect(() => {
    if ( document.hidden !== undefined ) {
      document.addEventListener( "visibilitychange", handleVisibilityChange, false );
      return () => {
        document.removeEventListener( "visibilitychange", handleVisibilityChange, false );
      }
    }
  });

  return (
    <Container {...props} tour={tour}>
      <Tabs>{tabs}</Tabs>
      <PortraitContainer>
        <ContainerInner>
          {bonus}
          {showChart && <Chart>{children}</Chart>}
          {ads && <Ads>{ads}</Ads>}
        </ContainerInner>
        {props.showControls && (
          <LandscapeControls>{controls}</LandscapeControls>
        )}
      </PortraitContainer>
      <Protection
        dealsToCompensate={dealsToCompensate}
        onCompensation={onCompensation}
        compensationUpdated={compensationUpdated}
        compensationCreated={compensationCreated}
        onCompensationCreated={onCompensationCreated}
        isDesktop={false}
        activeAccount={props.activeAccount}
      />
    </Container>
  );
}

function Portrait({
  tabs,
  children,
  controls,
  ads,
  bonus,
  dealsToCompensate,
  onCompensation,
  compensationUpdated,
  compensationCreated,
  onCompensationCreated,
  ...props
}: Props) {
  let tour = useContext(TourContext);
  const [showChart, setShowChart] = useState(true);

  const handleVisibilityChange = useCallback(()=>{
    if (document.hidden) {
      setShowChart(false);
    } else  {
      setShowChart(true);
    }
  },[]);

  useEffect(() => {
    if ( document.hidden !== undefined ) {
      document.addEventListener( "visibilitychange", handleVisibilityChange, false );
      return () => {
        document.removeEventListener( "visibilitychange", handleVisibilityChange, false );
      }
    }
  });

  return (
    <Container {...props} tour={tour}>
      {props.isIOS && props.appVersion >= 2000000 ? null : <Tabs>{tabs}</Tabs>}
      {bonus}
      {showChart && <Chart>{children}</Chart>}
      {ads && <Ads>{ads}</Ads>}
      {props.showControls && <React.Fragment>{controls}</React.Fragment>}
      <Protection
        dealsToCompensate={dealsToCompensate}
        onCompensation={onCompensation}
        compensationUpdated={compensationUpdated}
        compensationCreated={compensationCreated}
        onCompensationCreated={onCompensationCreated}
        isDesktop={false}
        activeAccount={props.activeAccount}
      />
    </Container>
  );
}

type State = {
  platform: string | null,
  browser: Object
};

class MobileLayout extends React.Component<Props, State> {
  state = {
    platform: null,
    browser: {}
  };

  componentDidMount() {
    get("/ajax/userInfo").then(({ platform, browser }) => {
      this.setState({
        platform,
        browser
      });
    });
    // TODO: .catch() please
  }

  render() {
    return (
      <Dimensions>
        {inner =>
          inner.width > 479 ? (
            <Landscape {...this.state} {...this.props} />
          ) : (
            <Portrait {...this.state} {...this.props} />
          )
        }
      </Dimensions>
    );
  }
}

export default MobileLayout;



// WEBPACK FOOTER //
// ./src/components/TradeMobile/TradeMobileLayout.js